/* eslint-disable react/display-name */
import React from 'react';
import Loadable from 'react-loadable';
import { Redirect } from 'react-router-dom';
import AppBackdrop from 'layouts/Backdrop';
import AuthLoading from 'layouts/AuthLoading';

const LoginPage = Loadable({
  loader: () => import('views/admin/Auth/LoginPage.jsx'),
  loading: () => <AuthLoading />,
});
const LandingPage = Loadable({
  loader: () => import('views/Landing/LandingPage.jsx'),
  loading: () => <AppBackdrop isOpen={true} />,
});
const Dashboard = Loadable({
  loader: () => import('views/admin/Dashboard/Dashboard.jsx'),
  loading: () => <AppBackdrop isOpen={true} />,
});

const SchoolPage = Loadable({
  loader: () => import('views/admin/School/SchoolPage'),
  loading: () => <AppBackdrop isOpen={true} />,
});
const SchoolAdminPage = Loadable({
  loader: () => import('views/admin/SchoolAdmin/SchoolAdminPage.jsx'),
  loading: () => <AppBackdrop isOpen={true} />,
});
// Teacher subjects is now a tab of School; the old address (links, bookmarks) goes there.
// The old School page had one tab each for batches, classes, subjects and teacher subjects; each is a sidebar page now,
// and old links (?subjects, ?teacher-subjects ...) land on the page that tab became.
const SchoolLegacyRedirect = ({ location }) => {
  const search = location?.search || '';
  // Longest name first: "teacher-subjects" also contains "subjects".
  if (search.includes('teacher-subjects')) {
    return <Redirect to="/admin/subjects?tab=teacher-subjects" />;
  }
  const target = search.includes('subjects') ? 'subjects' : search.includes('courses') ? 'courses' : 'batches';
  return <Redirect to={`/admin/${target}`} />;
};
const SchoolStaffHubPage = Loadable({
  loader: () => import('views/admin/SchoolStaff/SchoolStaffHubPage.jsx'),
  loading: () => <AppBackdrop isOpen={true} />,
});
const reportsPage = name =>
  Loadable({
    loader: () => import('views/admin/Reports/pages').then(m => m[name]),
    loading: () => <AppBackdrop isOpen={true} />,
  });
const ReportPages = {
  finance: reportsPage('FinanceReportsRoute'),
  transport: reportsPage('TransportReportsRoute'),
  unofficial: reportsPage('UnofficialReportsRoute'),
  common: reportsPage('CommonReportsRoute'),
  legacy: reportsPage('LegacyReportsRedirect'),
};
const Configuration = Loadable({
  loader: () => import('views/admin/Configuration'),
  loading: () => <AppBackdrop isOpen={true} />,
});
const MyProfile = Loadable({
  loader: () => import('views/admin/MyProfile'),
  loading: () => <AppBackdrop isOpen={true} />,
});
const FeesPage = Loadable({
  loader: () => import('views/admin/Fees/FeesPage'),
  loading: () => <AppBackdrop isOpen={true} />,
});

const CoursePage = Loadable({
  loader: () => import('views/admin/Course/CoursePage'),
  loading: () => <AppBackdrop isOpen={true} />,
});
const BatchPage = Loadable({
  loader: () => import('views/admin/Batch/BatchPage'),
  loading: () => <AppBackdrop isOpen={true} />,
});
const SubjectPage = Loadable({
  loader: () => import('views/admin/Subject/SubjectPage'),
  loading: () => <AppBackdrop isOpen={true} />,
});
const SubjectsHubPage = Loadable({
  loader: () => import('views/admin/Subject/SubjectsHubPage.jsx'),
  loading: () => <AppBackdrop isOpen={true} />,
});
// Teacher subjects is the second tab of the Subjects page now; the old address still works.
const TeacherSubjectsRedirect = () => <Redirect to="/admin/subjects?tab=teacher-subjects" />;

const StudentsPage = Loadable({
  loader: () => import('views/admin/Students/StudentsPage.jsx'),
  loading: () => <AppBackdrop isOpen={true} />,
});

const StudentViewPage = Loadable({
  loader: () => import('views/admin/Students/StudentViewPage.jsx'),
  loading: () => <AppBackdrop isOpen={true} />,
});

const GuardianPage = Loadable({
  loader: () => import('views/admin/Guardian/GuardianPage'),
  loading: () => <AppBackdrop isOpen={true} />,
});

const MyStudents = Loadable({
  loader: () => import('views/admin/MyStudents'),
  loading: () => <AppBackdrop isOpen={true} />,
});

const SchoolActivities = Loadable({
  loader: () => import('views/admin/Activities'),
  loading: () => <AppBackdrop isOpen={true} />,
});

const TimeTablePage = Loadable({
  loader: () => import('views/admin/TimeTable/TimeTablePage'),
  loading: () => <AppBackdrop isOpen={true} />,
});

const AdmissionPage = Loadable({
  loader: () => import('views/admin/Admissions/AdmissionPage.jsx'),
  loading: () => <AppBackdrop isOpen={true} />,
});

const Appreciations = Loadable({
  loader: () => import('views/admin/Appreciations'),
  loading: () => <AppBackdrop isOpen={true} />,
});

const StudentFeedbackPage = Loadable({
  loader: () => import('views/admin/StudentFeedback/StudentFeedbackPage'),
  loading: () => <AppBackdrop isOpen={true} />,
});

const AppreciationTemplate = Loadable({
  loader: () => import('views/admin/Appreciations/AppreciationTemplate'),
  loading: () => <AppBackdrop isOpen={true} />,
});

const CloneBatch = Loadable({
  loader: () => import('views/admin/CloneBatch/index'),
  loading: () => <AppBackdrop isOpen={true} />,
});

const AcademicYearSetup = Loadable({
  loader: () => import('views/admin/AcademicYearSetup/index'),
  loading: () => <AppBackdrop isOpen={true} />,
});

const AppThemeSelector = Loadable({
  loader: () => import('views/admin/Configuration/AppThemeSelector'),
  loading: () => <AppBackdrop isOpen={true} />,
});

const PaymentStatusPage = Loadable({
  loader: () => import('views/admin/Students/components/PaymentStatusPage'),
  loading: () => <AppBackdrop isOpen={true} />,
});

const BillingsPage = Loadable({
  loader: () => import('views/admin/Billings/BillingsPage.jsx'),
  loading: () => <AppBackdrop isOpen={true} />,
});
const AppUsagePage = Loadable({
  loader: () => import('views/admin/AppUsage/AppUsagePage.jsx'),
  loading: () => <AppBackdrop isOpen={true} />,
});

const UnofficialBillsPage = Loadable({
  loader: () => import('views/admin/UnofficialBills/UnofficialBillsPage.jsx'),
  loading: () => <AppBackdrop isOpen={true} />,
});

const StaffAttendanceHubPage = Loadable({
  loader: () => import('views/admin/StaffAttendance/StaffAttendanceHubPage.jsx'),
  loading: () => <AppBackdrop isOpen={true} />,
});

// Supersedes the old backend-less InventoryPurchases stub (views/admin/InventoryPurchases/) that
// used to live here commented out - the real inventory backend is built now, so this covers
// Categories/Items/Purchases/Stock/Transactions/Student Issues as tabs of one page instead.
const InventoryPage = Loadable({
  loader: () => import('views/admin/Inventory/InventoryPage.jsx'),
  loading: () => <AppBackdrop isOpen={true} />,
});

const TimelinePage = Loadable({
  loader: () => import('views/admin/Timeline/TimelinePage.jsx'),
  loading: () => <AppBackdrop isOpen={true} />,
});
const EmployeeActivityPage = Loadable({
  loader: () => import('views/admin/EmployeeActivities/EmployeeActivityPage'),
  loading: () => <AppBackdrop isOpen={true} />,
});
const NotificationsPage = Loadable({
  loader: () => import('views/admin/Notifications/NotificationsPage.jsx'),
  loading: () => <AppBackdrop isOpen={true} />,
});
const AnnouncementPages = {
  circulars: Loadable({
    loader: () => import('views/admin/Announcements').then(m => m.CircularsPage),
    loading: () => <AppBackdrop isOpen={true} />,
  }),
  noticeBoard: Loadable({
    loader: () => import('views/admin/Announcements').then(m => m.NoticeBoardRoutePage),
    loading: () => <AppBackdrop isOpen={true} />,
  }),
  eventsHolidays: Loadable({
    loader: () => import('views/admin/Announcements').then(m => m.EventsHolidaysPage),
    loading: () => <AppBackdrop isOpen={true} />,
  }),
};
const transportPage = name =>
  Loadable({
    loader: () => import('views/admin/Transport/pages').then(m => m[name]),
    loading: () => <AppBackdrop isOpen={true} />,
  });
const TransportPages = {
  buses: transportPage('BusesRoutePage'),
  drivers: transportPage('DriversRoutePage'),
  routes: transportPage('RoutesRoutePage'),
  assignment: transportPage('AssignmentRoutePage'),
  attendance: transportPage('BusAttendanceRoutePage'),
  legacy: transportPage('LegacyTransportRedirect'),
};

import constants, { employeeStatic } from 'config/constants';
// import UserAdmissionForm from 'views/admin/Admissions/components/UserAdmissionForm';

// Sidebar sections, in the order the school is built up: the school itself (years, classes, subjects), then its people
// (staff, students with their guardians), then the class work that needs them (time table, attendance, exams...), then what
// is announced, then money, transport, reports and settings. Items inside a section follow the same order of dependence.
// A section with no item the logged-in role may see is not shown; `label: null` is an item that stands alone.
export const sidebarGroups = [
  { key: 'home', label: null },
  { key: 'platform', label: 'Platform' },
  { key: 'school', label: 'School' },
  { key: 'people', label: 'People' },
  { key: 'classwork', label: 'Teaching' },
  { key: 'announcements', label: 'Announcements' },
  { key: 'finance', label: 'Finance' },
  { key: 'transport', label: 'Transport' },
  { key: 'reports', label: 'Reports' },
  { key: 'admin', label: 'Admin' },
];

const appRoutesList = [
  {
    path: '/',
    component: LandingPage,
    sidebarView: false,
    accessRoles: 'all',
  },
  {
    path: '/admin/dashboard',
    description:
      "Today at a glance: circulars, notice board, exams, events and holidays, with a reminder to add the year's public holidays.",
    name: 'DASHBOARD',
    icon: 'fas fa-home',
    component: Dashboard,
    sidebarView: true,
    sidebarGroup: 'home',
    sidebarOrder: 1,
    accessRoles: 'all',
  },
  {
    path: '/admin/schools',
    description: 'Add and manage the schools on the platform.',
    name: 'SCHOOLS',
    icon: 'fa fa-school',
    component: SchoolPage,
    sidebarView: true,
    sidebarGroup: 'platform',
    sidebarOrder: 1,
    accessRoles: `${constants.roles.SA}`,
  },
  {
    path: '/admin/school-admins',
    description: 'Create and manage school admin logins.',
    name: 'ADMINS',
    icon: 'fas fa-user',
    component: SchoolAdminPage,
    sidebarView: true,
    sidebarGroup: 'platform',
    sidebarOrder: 2,
    roleManagement: true,
    accessRoles: `${constants.roles.SA}`,
  },
  {
    // The old School page (tabs): old links land on the page that tab became.
    path: '/admin/our-school',
    name: 'SCHOOL',
    component: SchoolLegacyRedirect,
    sidebarView: false,
    accessRoles: `${constants.roles.SA},${constants.roles.admin},${constants.roles.teacher}`,
  },
  {
    path: '/admin/school-activities',
    description: 'Follow tasks and requests raised in the school, plus voice messages and calls.',
    name: 'SCHOOL ACTIVITIES',
    icon: 'fas fa-address-card',
    component: SchoolActivities,
    sidebarView: true,
    sidebarGroup: 'announcements',
    sidebarOrder: 3,
    roleManagement: true,
    accessRoles: `${constants.roles.SA},${constants.roles.admin},${constants.roles.teacher}`,
  },
  {
    path: '/admin/notifications',
    description: 'Messages sent to you from the school, with unread ones highlighted.',
    name: 'NOTIFICATIONS',
    icon: 'fas fa-bell',
    component: NotificationsPage,
    sidebarView: true,
    sidebarGroup: 'announcements',
    sidebarOrder: 4,
    roleManagement: true,
    accessRoles: `${constants.roles.SA},${constants.roles.admin},${constants.roles.teacher}`,
  },
  {
    path: '/admin/circulars',
    description: 'Send circulars to the school and read the ones already sent, newest first.',
    name: 'CIRCULARS',
    icon: 'fas fa-bullhorn',
    component: AnnouncementPages.circulars,
    sidebarView: true,
    sidebarGroup: 'announcements',
    sidebarOrder: 2,
    roleManagement: true,
    accessRoles: `${constants.roles.SA},${constants.roles.admin},${constants.roles.teacher}`,
  },
  {
    path: '/admin/notice-board',
    description: 'Post notices and read the earlier ones, newest first.',
    name: 'NOTICE BOARD',
    icon: 'fas fa-clipboard',
    component: AnnouncementPages.noticeBoard,
    sidebarView: true,
    sidebarGroup: 'announcements',
    sidebarOrder: 1,
    roleManagement: true,
    accessRoles: `${constants.roles.SA},${constants.roles.admin},${constants.roles.teacher}`,
  },
  {
    path: '/admin/events-holidays',
    description: 'School events, holidays and meetings.',
    name: 'EVENTS AND HOLIDAYS',
    icon: 'fas fa-calendar-alt',
    component: AnnouncementPages.eventsHolidays,
    sidebarView: true,
    sidebarGroup: 'announcements',
    sidebarOrder: 5,
    roleManagement: true,
    accessRoles: `${constants.roles.SA},${constants.roles.admin},${constants.roles.teacher}`,
  },
  {
    path: '/admin/batches',
    name: 'BATCHES',
    icon: 'fas fa-chalkboard-teacher',
    component: BatchPage,
    description: 'The academic years (batches) of the school.',
    sidebarView: true,
    sidebarGroup: 'school',
    sidebarOrder: 1,
    accessRoles: `${constants.roles.SA},${constants.roles.admin},${constants.roles.teacher}`,
  },
  {
    path: '/admin/clone-batch',
    name: 'CLONE BATCH',
    icon: '',
    component: CloneBatch,
    sidebarView: false,
    accessRoles: `${constants.roles.SA},${constants.roles.admin}`,
  },
  {
    path: '/admin/courses',
    name: 'CLASSES',
    icon: 'fa fa-layer-group',
    component: CoursePage,
    description: 'The classes of the selected academic year.',
    sidebarView: true,
    sidebarGroup: 'school',
    sidebarOrder: 2,
    accessRoles: `${constants.roles.SA},${constants.roles.admin},${constants.roles.teacher}`,
  },
  {
    path: '/admin/subjects',
    name: 'SUBJECTS',
    icon: 'fas fa-book-open',
    component: SubjectsHubPage,
    description: `The subjects of each class, and which ${employeeStatic?.toLowerCase()} teaches each subject.`,
    sidebarView: true,
    sidebarGroup: 'school',
    sidebarOrder: 3,
    accessRoles: `${constants.roles.SA},${constants.roles.admin},${constants.roles.teacher}`,
  },
  {
    // Hardcoded name, not built on employeeStatic/employeesStatic - unlike the config-driven
    // Teacher label elsewhere, School Staffs (and its Teachers/Workers tabs) is meant to read the
    // same for every school regardless of that config.
    path: '/admin/school-staffs',
    description: 'Teachers and workers: view, add and edit their details, with head counts of the school staff.',
    name: 'SCHOOL STAFFS',
    icon: 'fas fa-users-cog',
    component: SchoolStaffHubPage,
    sidebarView: true,
    sidebarGroup: 'people',
    sidebarOrder: 1,
    accessRoles: `${constants.roles.SA},${constants.roles.admin}`,
  },
  {
    path: '/admin/staff-attendance',
    description:
      'Mark and review teacher and worker attendance, set staff time allowance, approve or reject staff leaves and set the leave types.',
    name: 'STAFF ATTENDANCE',
    icon: 'fas fa-user-clock',
    component: StaffAttendanceHubPage,
    sidebarView: true,
    sidebarGroup: 'people',
    sidebarOrder: 2,
    accessRoles: `${constants.roles.SA},${constants.roles.admin}`,
  },
  {
    path: '/admin/teacher-subjects',
    name: `${employeeStatic?.toUpperCase()} SUBJECTS`,
    component: TeacherSubjectsRedirect,
    sidebarView: false,
    accessRoles: `${constants.roles.SA},${constants.roles.admin},${constants.roles.teacher}`,
  },

  {
    path: '/admin/employee-activities',
    description: `What ${employeeStatic?.toLowerCase()}s handle in a class: time table, attendance, homework and assignments (newest first), online classes and marks (by student, or the whole class on one marks sheet).`,
    roleDescriptions: {
      [constants.roles.teacher]: 'Your class work: time table, attendance, homework, online classes and marks.',
    },
    name: `${employeeStatic?.toUpperCase()} ACTIVITIES`,
    icon: 'fas fa-chalkboard-teacher',
    component: EmployeeActivityPage,
    sidebarView: true,
    sidebarGroup: 'classwork',
    sidebarOrder: 1,
    roleManagement: true,
    accessRoles: `${constants.roles.SA},${constants.roles.admin},${constants.roles.teacher}`,
  },
  {
    path: '/admin/guardians',
    name: 'GUARDIANS',
    icon: 'fas fa-user',
    component: GuardianPage,
    sidebarView: false,
    roleManagement: true,
    accessRoles: `${constants.roles.SA},${constants.roles.admin},${constants.roles.teacher}`,
  },
  {
    path: '/admin/students',
    name: 'STUDENTS',
    icon: 'fas fa-user-graduate',
    component: StudentsPage,
    sidebarView: false,
    accessRoles: `${constants.roles.SA},${constants.roles.admin},${constants.roles.teacher}`,
  },
  {
    path: '/admin/students/:studentId/view',
    name: 'STUDENTS',
    icon: 'fas fa-user-graduate',
    component: StudentViewPage,
    sidebarView: false,
    accessRoles: `${constants.roles.SA},${constants.roles.admin},${constants.roles.teacher}`,
  },
  {
    path: '/admin/school-students',
    description:
      'Students and their guardians: head counts of the school and the class, view, add, edit, add parent notes, and switch a student to another class.',
    roleDescriptions: { [constants.roles.teacher]: 'Look up students and their guardians.' },
    name: 'STUDENTS',
    icon: 'fas fa-user-graduate',
    component: MyStudents,
    sidebarView: true,
    sidebarGroup: 'people',
    sidebarOrder: 3,
    // The student detail view lives under a separate /admin/students/:id/view path (not
    // under /admin/school-students), so the sidebar wouldn't otherwise highlight "Students"
    // while looking at one - this keeps it lit up for that case too.
    activeAliases: ['/admin/students'],
    accessRoles: `${constants.roles.SA},${constants.roles.admin},${constants.roles.teacher}`,
  },
  {
    path: '/admin/fees',
    description: 'Class fees, student fees, concessions and transport fees.',
    name: 'FEES',
    icon: 'fas fa-credit-card',
    component: FeesPage,
    sidebarView: true,
    sidebarGroup: 'finance',
    sidebarOrder: 1,
    roleManagement: true,
    accessRoles: `${constants.roles.SA},${constants.roles.admin},${constants.roles.teacher}`,
  },
  {
    path: '/admin/billings',
    description: 'Extra charges billed to students, with type, amount and due date.',
    name: 'BILLINGS',
    icon: 'fas fa-file-invoice-dollar',
    component: BillingsPage,
    sidebarView: true,
    sidebarGroup: 'finance',
    sidebarOrder: 2,
    roleManagement: true,
    accessRoles: `${constants.roles.SA},${constants.roles.admin},${constants.roles.teacher}`,
  },
  {
    // Bills for a student or for anyone else (a typed name), collected outside the official fee books. Admin role
    // only (not super admin or teachers); the page also turns other roles away, since this isn't a feature route.
    path: '/admin/unofficial-bills',
    description: 'Money collected outside the official bills, from a student or anyone else, with receipts.',
    name: 'UNOFFICIAL BILLS',
    icon: 'fas fa-receipt',
    component: UnofficialBillsPage,
    sidebarView: true,
    sidebarGroup: 'finance',
    sidebarOrder: 3,
    accessRoles: constants.roles.admin,
  },
  {
    // Who uses the mobile app: version (latest or not), last login, and Active / Inactive accounts.
    // Admin role only; the page also turns other roles away, since this isn't a feature route.
    path: '/admin/app-usage',
    description:
      'See who uses the mobile app, which version they run and when they last logged in. Mark an account inactive (for example a teacher who left) so it can no longer log in.',
    name: 'APP USAGE',
    icon: 'fas fa-mobile-alt',
    component: AppUsagePage,
    sidebarView: true,
    sidebarGroup: 'admin',
    sidebarOrder: 5,
    accessRoles: constants.roles.admin,
  },
  {
    // Class-wise posts (text, photos, videos) that guardians see in the mobile app. Admin and teachers only:
    // the timeline backend has no super-admin user type.
    path: '/admin/timeline',
    description: 'View what teachers post to each class, and the comments.',
    roleDescriptions: {
      [constants.roles.teacher]: 'Post updates, photos and videos to your classes and reply to comments.',
    },
    name: 'TIMELINE',
    icon: 'fas fa-stream',
    component: TimelinePage,
    sidebarView: true,
    sidebarGroup: 'classwork',
    sidebarOrder: 2,
    accessRoles: `${constants.roles.admin},${constants.roles.teacher}`,
  },
  {
    path: '/admin/inventory',
    description: 'Items in stock, purchases, and items issued to students.',
    name: 'INVENTORY',
    icon: 'fas fa-boxes',
    component: InventoryPage,
    sidebarView: true,
    sidebarGroup: 'finance',
    sidebarOrder: 4,
    accessRoles: `${constants.roles.SA},${constants.roles.admin}`,
  },
  {
    // The old single Transport page (tabs): old links land on the page that tab became.
    path: '/admin/transport',
    name: 'TRANSPORT',
    component: TransportPages.legacy,
    sidebarView: false,
    accessRoles: constants.roles.admin,
  },
  {
    path: '/admin/transport/buses',
    description: 'Add and manage the school buses and their documents.',
    name: 'BUSES',
    icon: 'fas fa-bus',
    component: TransportPages.buses,
    sidebarView: true,
    sidebarGroup: 'transport',
    sidebarOrder: 1,
    // Admin role only (rolled out to every admin; it was behind a per-user feature switch while it was tested).
    accessRoles: constants.roles.admin,
  },
  {
    path: '/admin/transport/drivers',
    description: 'Add and manage the bus drivers.',
    name: 'DRIVERS',
    icon: 'fas fa-id-card',
    component: TransportPages.drivers,
    sidebarView: true,
    sidebarGroup: 'transport',
    sidebarOrder: 2,
    // Admin role only (rolled out to every admin; it was behind a per-user feature switch while it was tested).
    accessRoles: constants.roles.admin,
  },
  {
    path: '/admin/transport/routes',
    description: 'Bus routes and the stops each one serves, with the number of students riding the transport.',
    name: 'ROUTES AND STOPS',
    icon: 'fas fa-route',
    component: TransportPages.routes,
    sidebarView: true,
    sidebarGroup: 'transport',
    sidebarOrder: 3,
    // Admin role only (rolled out to every admin; it was behind a per-user feature switch while it was tested).
    accessRoles: constants.roles.admin,
  },
  {
    path: '/admin/transport/assignment',
    description: 'Put a driver on a bus for each route, and add the students who ride each route.',
    name: 'BUS ASSIGNMENT',
    icon: 'fas fa-people-arrows',
    component: TransportPages.assignment,
    sidebarView: true,
    sidebarGroup: 'transport',
    sidebarOrder: 4,
    // Admin role only (rolled out to every admin; it was behind a per-user feature switch while it was tested).
    accessRoles: constants.roles.admin,
  },
  {
    path: '/admin/transport/attendance',
    description:
      'Tick which students were picked up or dropped by a bus, or were absent - the same boxes as the driver in the app.',
    name: 'BUS ATTENDANCE',
    icon: 'fas fa-clipboard-check',
    component: TransportPages.attendance,
    sidebarView: true,
    sidebarGroup: 'transport',
    sidebarOrder: 5,
    accessRoles: constants.roles.admin,
  },
  {
    // The old single Reports page (tabs): old links land on the page that tab became.
    path: '/admin/reports',
    name: 'REPORTS',
    component: ReportPages.legacy,
    sidebarView: false,
    accessRoles: `${constants.roles.SA},${constants.roles.admin},${constants.roles.teacher}`,
  },
  {
    path: '/admin/reports/finance',
    description: 'Fee collection, dues and other finance reports, with Excel export.',
    name: 'FINANCE REPORTS',
    icon: 'fas fa-coins',
    component: ReportPages.finance,
    sidebarView: true,
    sidebarGroup: 'reports',
    sidebarOrder: 1,
    roleManagement: true,
    accessRoles: `${constants.roles.SA},${constants.roles.admin}`,
  },
  {
    path: '/admin/reports/transport',
    description:
      'Bus pickup and drop by day, week or month, and route (stops with distances, bus and driver), bus and driver reports.',
    name: 'TRANSPORT REPORTS',
    icon: 'fas fa-route',
    component: ReportPages.transport,
    sidebarView: true,
    sidebarGroup: 'reports',
    sidebarOrder: 2,
    roleManagement: true,
    accessRoles: constants.roles.admin,
  },
  {
    path: '/admin/reports/unofficial',
    description: 'Money collected outside the official bills, by month, week or date range.',
    name: 'UNOFFICIAL BILLS REPORT',
    icon: 'fas fa-receipt',
    component: ReportPages.unofficial,
    sidebarView: true,
    sidebarGroup: 'reports',
    sidebarOrder: 3,
    roleManagement: true,
    accessRoles: constants.roles.admin,
  },
  {
    path: '/admin/reports/common',
    description: 'School, class, student and staff reports.',
    name: 'COMMON REPORTS',
    icon: 'fas fa-file-alt',
    component: ReportPages.common,
    sidebarView: true,
    sidebarGroup: 'reports',
    sidebarOrder: 4,
    roleManagement: true,
    accessRoles: `${constants.roles.SA},${constants.roles.admin},${constants.roles.teacher}`,
  },
  {
    path: '/admin/admissions',
    description: 'Admission enquiries and applications, and where each one stands.',
    name: 'ADMISSIONS',
    icon: 'fas fa-users',
    component: AdmissionPage,
    sidebarView: true,
    sidebarGroup: 'people',
    sidebarOrder: 4,
    roleManagement: true,
    accessRoles: `${constants.roles.SA},${constants.roles.admin},${constants.roles.teacher}`,
  },
  {
    path: '/admin/generate-time-table',
    description: 'Build the class time table: allocate subjects and teachers, and fix clashes.',
    name: 'GENERATE TIME TABLE',
    icon: 'fas fa-calendar',
    component: TimeTablePage,
    sidebarView: true,
    sidebarGroup: 'admin',
    sidebarOrder: 3,
    roleManagement: true,
    accessRoles: `${constants.roles.SA},${constants.roles.admin},${constants.roles.teacher}`,
  },
  {
    path: '/admin/academic-year-setup',
    description: 'Step-by-step setup of a new academic year: classes, subjects, teachers and students.',
    name: 'NEW ACADEMIC YEAR',
    icon: 'fas fa-calendar-plus',
    component: AcademicYearSetup,
    sidebarView: true,
    sidebarGroup: 'admin',
    sidebarOrder: 4,
    roleManagement: true,
    accessRoles: `${constants.roles.SA},${constants.roles.admin}`,
  },
  {
    path: '/admin/appreciations',
    description: 'Create appreciation cards for classes, students or teachers.',
    name: `APPRECIATIONS`,
    icon: 'fas fa-award',
    component: Appreciations,
    sidebarView: true,
    sidebarGroup: 'announcements',
    sidebarOrder: 6,
    roleManagement: true,
    accessRoles: `${constants.roles.SA},${constants.roles.admin},${constants.roles.teacher}`,
  },
  {
    path: '/admin/student-feedback',
    description: "Send a note about a student straight to that student's guardian.",
    name: 'PARENT NOTES',
    icon: 'fas fa-comment-dots',
    component: StudentFeedbackPage,
    sidebarView: true,
    sidebarGroup: 'announcements',
    sidebarOrder: 7,
    roleManagement: true,
    accessRoles: `${constants.roles.SA},${constants.roles.admin},${constants.roles.teacher}`,
  },
  {
    path: '/admin/appreciations/appreciation-template',
    name: `APPRECIATIONS`,
    icon: 'fas fa-award',
    component: AppreciationTemplate,
    sidebarView: false,
    roleManagement: true,
    accessRoles: `${constants.roles.SA},${constants.roles.admin},${constants.roles.teacher}`,
  },
  {
    path: '/admin/settings',
    description: 'School profile, common settings, permissions and attendance rules.',
    name: 'SCHOOL SETTINGS',
    icon: 'fas fa-cog',
    component: Configuration,
    sidebarView: true,
    sidebarGroup: 'admin',
    sidebarOrder: 1,
    roleManagement: true,
    accessRoles: `${constants.roles.SA},${constants.roles.admin},${constants.roles.teacher}`,
  },
  // {
  //   path: '/admin/help-feedback',
  //   name: 'HELP & FEEDBACK',
  //   icon: 'fas fa-question-circle',
  //   component: FeesPage,
  //   sidebarView: true,
  //   roleManagement: true,
  //   accessRoles: `${constants.roles.SA},${constants.roles.admin},${constants.roles.teacher}`,
  // },
  {
    path: '/admin/my-profile',
    name: 'MY PROFILE',
    icon: '',
    component: MyProfile,
    sidebarView: false,
    roleManagement: true,
    accessRoles: `${constants.roles.SA},${constants.roles.admin},${constants.roles.teacher}`,
  },
  {
    path: '/admin/app-settings',
    name: 'APP SETTINGS',
    component: AppThemeSelector,
    icon: '',
    sidebarView: false,
    roleManagement: true,
    accessRoles: `${constants.roles.SA},${constants.roles.admin},${constants.roles.teacher}`,
  },
  {
    path: '/admin/payment-status/:paymentOrderId/:paymentStatus/:amount',
    component: PaymentStatusPage,
    sidebarView: false,
    accessRoles: 'all',
    isGuest: true,
  },
  {
    path: '/admin/login',
    component: LoginPage,
    sidebarView: false,
    accessRoles: 'all',
  },
  // {
  //   path: '/user/admission-form/:schoolId/:phoneNumber/:admissionId',
  //   component: UserAdmissionForm,
  //   sidebarView: false,
  //   accessRoles: 'all',
  // },
];

export default appRoutesList;
